import React, { Component } from 'react';
import { connect } from 'react-redux';

import { ROOT_URL_V2 } from '../../../config';
import actions from '../../../actions';
import ActionButton from '../../common/ActionButton';
import PopupHeader from '../../common/PopupHeader';
import LoadingSpinner from '../../common/LoadingSpinner';
import history from '../../../history';
import { TDispatch, TRootState } from '../../../reducers';
import { TServicesStructure } from '../../../types';

const STATUS_CODE_FORBIDDEN = 403;

type TUploadingFreelancers = {
    uploading: boolean;
    error: any;
    uploaded: boolean;
    meta: {
        status_code: number;
    };
    payload: {
        created: number;
        attached: number;
        skipped: number;
    };
};

type TUploadFreelancersContainerProps = {
    dismiss: () => void;
    servicesStructure: TServicesStructure;
    uploadFreelancersAsync: (
        service: string,
        skipInvites: boolean,
        csvFile: any,
    ) => void;
    uploadingFreelancers: TUploadingFreelancers;
};

type TUploadFreelancersContainerState = {
    selectedService: string;
    tryAgain: boolean;
    skipInvites: boolean;
};

class UploadFreelancersContainer extends Component<
    TUploadFreelancersContainerProps,
    TUploadFreelancersContainerState
> {
    fileRef = React.createRef<HTMLInputElement>();

    state: TUploadFreelancersContainerState = {
        selectedService: null,
        tryAgain: false,
        skipInvites: false,
    };

    onDismiss = () => {
        history.push('/freelancers');
    };

    onClose = () => {
        this.setState(
            {
                tryAgain: true,
                selectedService: null,
            },
            this.props.dismiss,
        );
    };

    setSelectedService = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            selectedService: event.target.value,
        });
    };

    handleFileUpload = () => {
        this.setState({
            tryAgain: false,
        });

        this.props.uploadFreelancersAsync(
            this.state.selectedService,
            this.state.skipInvites,
            this.fileRef.current.files[0],
        );
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            skipInvites: event.target.checked,
        });
    };

    retryAgain = () => {
        this.setState({
            tryAgain: true,
        });
    };

    renderUploadSuccess() {
        let result;
        const { payload } = this.props.uploadingFreelancers;

        if (payload.created && payload.attached) {
            result = `${
                payload.created + payload.attached
            } workers have been imported,
                ${
                    payload.attached
                } of them already had an account in the system.`;
        } else if (payload.created) {
            result = `${payload.created} workers have been created in the system.`;
        } else if (payload.attached) {
            result = `${payload.attached} workers have been imported, all of them already had an account in the system.`;
        } else if (payload.skipped) {
            result = `${payload.skipped} workers have been ignored.`;
        } else {
            result = 'No data was found in file.';
        }

        return (
            <div className="upload-freelancer-modal">
                <PopupHeader
                    title="Import workers via CSV"
                    onDismiss={this.onClose}
                />
                <div>
                    <p>Successfully processed file.</p>
                    <p>{result}</p>
                </div>
                <ActionButton onAction={this.onClose} text="Close" />
            </div>
        );
    }

    renderUploadError = () => {
        let errorDetail;
        let errorInfo;

        const { uploadingFreelancers } = this.props;

        if (uploadingFreelancers.meta.status_code === STATUS_CODE_FORBIDDEN) {
            errorDetail = <p>{uploadingFreelancers.error}</p>;
        } else if (uploadingFreelancers.error.freelancer_csv) {
            errorDetail = <p>{uploadingFreelancers.error.freelancer_csv}</p>;
        } else {
            errorInfo = `Cannot import the CSV file.
                The following rows have errors, please fix these and try again.`;
            errorDetail = Object.keys(uploadingFreelancers.error).map(
                (line, index) => (
                    <dl key={`dl-${line}`}>
                        <dt key={`dt-${line}`}>{line}</dt>
                        {Object.keys(uploadingFreelancers.error[line]).map(
                            field =>
                                uploadingFreelancers.error[line][field].map(
                                    (value: string, valueIndex: number) => (
                                        // eslint-disable-next-line react/no-array-index-key
                                        <dd
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`${field}-${index}-${valueIndex}`}
                                        >
                                            {field}: {value}
                                        </dd>
                                    ),
                                ),
                        )}
                    </dl>
                ),
            );
        }

        return (
            <div className="upload-freelancer-modal">
                <PopupHeader
                    title="Import workers via CSV"
                    onDismiss={this.onDismiss}
                />
                <div>
                    <p className="error">{errorInfo}</p>
                    <div className="errorList">{errorDetail}</div>
                    <ActionButton onAction={this.retryAgain} text="Try again" />
                </div>
            </div>
        );
    };

    renderUploading = () => (
        <div className="upload-freelancer-modal">
            <PopupHeader
                title="Import workers via CSV"
                onDismiss={this.onDismiss}
            />
            <LoadingSpinner className="small" />
        </div>
    );

    getExampleFileUrl = (serviceKey: string) =>
        `${ROOT_URL_V2}freelancers/import?service=${serviceKey}&extract=1`;

    renderServiceOptions = () => this.props.servicesStructure &&
        Object.entries(this.props.servicesStructure)
            .filter(([serviceKey]) => !!serviceKey)
            .map(([serviceKey, serviceData]) => (
                <div key={serviceKey}>
                    <label>
                        <input
                            className="serviceOptionRadio radio-purple"
                            type="radio"
                            value={serviceKey}
                            name="service"
                        />
                        {serviceData.label}
                    </label>
                    <a href={this.getExampleFileUrl(serviceKey)}>(Download example file)</a>
                </div>
            ));

    renderUploadCSVInfo = () => (
        <div className="upload-freelancer-modal">
            <PopupHeader
                title="Import workers via CSV"
                onDismiss={this.onDismiss}
            />
            <div>
                <p>1. Select the type of workers you would like to upload</p>
                <div onChange={this.setSelectedService.bind(this)}>
                    {this.renderServiceOptions()}
                </div>
                <div>
                    <input
                        className="serviceOptionRadio checkbox-purple"
                        id="checkboxSkipInvites"
                        type="checkbox"
                        name="skipInvites"
                        checked={this.state.skipInvites}
                        onChange={this.handleInputChange}
                    />
                    <label htmlFor="checkboxSkipInvites">Invite silently</label>
                </div>
            </div>
            <div className="modal-bottom-bar">
                <p>2. Choose a CSV file containing data for your workers</p>
                <input
                    type="file"
                    ref={this.fileRef}
                    name="file"
                    accept=".csv"
                    id="inputFile"
                    onChange={this.handleFileUpload}
                    disabled={!this.state.selectedService}
                />
                <label htmlFor="inputFile" className="table-action-button">
                    Select file...
                </label>
            </div>
        </div>
    );

    render() {
        if (this.state.tryAgain) {
            return this.renderUploadCSVInfo();
        }

        if (this.props.uploadingFreelancers.uploading) {
            return this.renderUploading();
        }

        if (this.props.uploadingFreelancers.error) {
            return this.renderUploadError();
        }

        if (
            this.props.uploadingFreelancers.uploaded &&
            this.props.uploadingFreelancers.payload !== null
        ) {
            return this.renderUploadSuccess();
        }

        return this.renderUploadCSVInfo();
    }
}

function mapStateToProps(state: TRootState) {
    return {
        servicesStructure: state.servicesStructure,
        uploadingFreelancers: state.uploadingFreelancers,
    };
}

function mapDispatchToProps(dispatch: TDispatch) {
    return {
        uploadFreelancersAsync: (
            service: string,
            skipInvites: boolean,
            csvFile: any,
        ) =>
            dispatch(
                actions.uploadFreelancers({
                    service,
                    skipInvites,
                    csvFile,
                }),
            ),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(UploadFreelancersContainer);
